import React, { useEffect, useRef } from 'react'
import { designTokens } from '@/theme'
import SecondaryButton from '@/components/common/SecondaryButton'
import { readStoredPaymentStorefrontBase } from '@/domain/payment/payment-await-session'
import { storefrontJoin } from '@/utils/storefront-paths'

function PaymentGatewayResponse() {
    const params = new URLSearchParams(window.location.search)
    const typeRaw = params.get('responseType') || ''
    const [type, typeQueryRemainder] = typeRaw.split('?')
    const orderId =
        params.get('order_id') ||
        (typeQueryRemainder ? new URLSearchParams(typeQueryRemainder).get('order_id') : null)

    const postedRef = useRef(false)

    useEffect(() => {
        // When this page is opened inside a popup, notify the opener and auto-close.
        // This keeps backend/webhook as the source of truth; the opener should verify status via existing APIs.
        if (postedRef.current) return
        postedRef.current = true

        const payload = {
            type: 'GMF_PAYMENT_CALLBACK',
            responseType: type || '',
            order_id: orderId || '',
        }

        const opener = window.opener
        const targetOrigin = window.location.origin

        try {
            let attempts = 0
            const maxAttempts = 8
            const timer = window.setInterval(() => {
                attempts += 1
                try {
                    if (opener && !opener.closed) {
                        opener.postMessage(payload, targetOrigin)
                    }
                } catch {
                    // ignore
                }

                if (attempts >= maxAttempts) {
                    window.clearInterval(timer)
                    // Same-tab return: no opener → redirect to recover to verify via backend.
                    if (!opener && orderId) {
                        const storedBase = readStoredPaymentStorefrontBase()
                        if (storedBase != null) {
                            const recoverPath = storefrontJoin(
                                storedBase,
                                '/checkout/recover?orderId=' + encodeURIComponent(orderId)
                            )
                            window.location.replace(recoverPath)
                        }
                    } else {
                        window.setTimeout(() => window.close(), 50)
                    }
                }
            }, 250)
        } catch {
            // If postMessage fails for any reason, user can still close manually.
        }
    }, [type, orderId])

    const handleClose = () => {
        window.close()
    }

    const isSuccess = type.toLowerCase() === 'success'
    const isFailure = ['failed', 'error'].includes(type.toLowerCase())

    const backgroundColor = isSuccess
        ? 'linear-gradient(135deg, #a8ff78 0%, #78ffd6 100%)'
        : isFailure
            ? 'linear-gradient(135deg, #ff9a9e 0%, #fad0c4 100%)'
            : 'linear-gradient(135deg, #89f7fe 0%, #66a6ff 100%)'

    const emoji = isSuccess ? '🎉' : isFailure ? '😞' : '💳'

    return (
        <div
            style={{
                height: '100vh',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                background: backgroundColor,
                fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif",
                transition: `all ${designTokens.motion.slow}`,
                color: '#333',
                textAlign: 'center',
                padding: '2rem',
            }}
        >
            <div
                style={{
                    background: 'white',
                    borderRadius: `${designTokens.shape.borderRadius.lg}px`,
                    boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                    padding: '3rem 4rem',
                    maxWidth: '480px',
                    width: '90%',
                    animation: 'fadeIn 0.6s ease-out',
                }}
            >
                <h1 style={{ fontSize: '2rem', marginBottom: '1rem' }}>
                    {emoji} Payment {type ? type.toUpperCase() : 'Response'}
                </h1>
                {orderId && (
                    <p style={{ fontSize: '1rem', color: '#888', marginBottom: '1.5rem' }}>
                        Order ID: <strong>{orderId}</strong>
                    </p>
                )}
                <p style={{ fontSize: '1.1rem', color: '#666', marginBottom: '2rem' }}>
                    {isSuccess
                        ? 'Your payment was successful! Thank you for your order.'
                        : isFailure
                            ? 'We couldn’t process your payment. Please try again or contact support.'
                            : 'Processing your payment response...'}
                </p>
                <SecondaryButton onClick={handleClose}>
                    Close Window
                </SecondaryButton>
            </div>
        </div>
    )
}

export default PaymentGatewayResponse
